<template>
    <div class="agco-health-card" :class="{ dark: isDark }">
        <div class="card-header">
            <h3>AGCO Compliance Submissions</h3>
            <router-link class="view-link" to="/agco-logs/warehouses">View logs</router-link>
        </div>
        <div v-if="loading" class="card-message">Loading...</div>
        <div v-else-if="error" class="card-message">Could not load AGCO submission status.</div>
        <div v-else>
            <div v-if="!stores.length" class="card-message">No stores found.</div>
            <table v-else class="health-table">
                <thead>
                    <tr>
                        <th>Store</th>
                        <th>Inventory</th>
                        <th>Position</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="store of stores" :key="store.id">
                        <td>{{ store.warehouseName }}</td>
                        <td>
                            <span :class="['badge', badgeClass(store.events.inventory)]" :title="eventTitle(store.events.inventory)">
                                {{ badgeLabel(store.events.inventory) }}
                            </span>
                        </td>
                        <td>
                            <span :class="['badge', badgeClass(store.events.position)]" :title="eventTitle(store.events.position)">
                                {{ badgeLabel(store.events.position) }}
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-if="hasProblem" class="problem-note">
                One or more AGCO submissions are failing or overdue — compliance reports may not be reaching the AGCO.
            </div>
            <div v-if="hasSeededData" class="qa-note">
                Local QA seed data is shaping these AGCO statuses.
            </div>
        </div>
    </div>
</template>

<script>
import { http } from "../../utils/http"
import { formatBusinessInstant } from "../../utils/business-date"

export default {
    name: "AgcoHealthCard",
    props: {
        isDark: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: true,
            error: false,
            stores: [],
            fetchRequestId: 0,
        }
    },
    computed: {
        hasProblem() {
            return this.stores.some((store) => !store.healthy)
        },
        hasSeededData() {
            return this.stores.some((store) =>
                Object.values(store.events || {}).some((event) => event && event.qaSeeded)
            )
        },
    },
    mounted() {
        this.fetchHealth()
    },
    methods: {
        async fetchHealth() {
            const requestId = ++this.fetchRequestId
            this.loading = true
            this.error = false
            try {
                const res = await http.get("/agco-logs/health")
                if (requestId !== this.fetchRequestId) return
                this.stores = res.data.result || []
                if (typeof this.$emit === "function") {
                    this.$emit("data-loaded", { status: "loaded", rows: this.stores })
                }
            } catch (err) {
                if (requestId !== this.fetchRequestId) return
                console.error("Error fetching AGCO health:", err)
                this.error = true
                if (typeof this.$emit === "function") {
                    this.$emit("data-loaded", { status: "error", rows: [] })
                }
            } finally {
                if (requestId === this.fetchRequestId) {
                    this.loading = false
                }
            }
        },
        badgeClass(event) {
            if (!event) return "badge-na"
            if (event.failed) return "badge-error"
            if (event.stale) return "badge-stale"
            return "badge-ok"
        },
        badgeLabel(event) {
            if (!event || (!event.lastRunAt && !event.lastSuccessAt)) return "No data"
            if (event.failed) return "Failed"
            if (event.stale) return "Overdue"
            return "OK"
        },
        eventTitle(event) {
            if (!event) return ""
            const parts = []
            if (event.lastRunAt) parts.push(`Last run: ${formatBusinessInstant(event.lastRunAt)}`)
            if (event.lastSuccessAt) parts.push(`Last success: ${formatBusinessInstant(event.lastSuccessAt)}`)
            if (event.message) parts.push(`Message: ${event.message}`)
            if (event.qaSeeded) parts.push("Source: local QA seed")
            return parts.join("\n")
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.agco-health-card {
    background-color: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    padding: 14px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);

    &.dark {
        background-color: $darkCardBg;
        border-color: $darkBorderColor;
        color: $darkTextColor;
    }
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;

    h3 {
        font-size: 15px;
        font-weight: 600;
        color: $primaryColor;
        margin: 0;

        .dark & {
            color: $secondaryColorDark;
        }
    }

    .view-link {
        font-size: 13px;
        text-decoration: none;
        color: $primaryColor;

        &:hover {
            text-decoration: underline;
        }
    }
}

.card-message {
    padding: 10px 0;
    opacity: 0.7;
    font-size: 14px;
}

.health-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        text-align: left;
        padding: 6px 8px;
        opacity: 0.7;
        font-weight: 600;
    }

    td {
        padding: 6px 8px;
        border-top: 1px solid $lightBorderColor;

        .dark & {
            border-top-color: $darkBorderColor;
        }
    }
}

.badge {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
}

.badge-ok {
    background: rgba(46, 160, 67, 0.15);
    color: #2ea043;
}

.badge-error {
    background: rgba(218, 54, 51, 0.15);
    color: #da3633;
}

.badge-stale {
    background: rgba(210, 153, 34, 0.18);
    color: #b07d12;
}

.badge-na {
    background: rgba(128, 128, 128, 0.15);
    color: #808080;
}

.problem-note {
    margin-top: 10px;
    padding: 8px 10px;
    border-radius: 6px;
    font-size: 13px;
    background: rgba(218, 54, 51, 0.08);
    color: #da3633;
}

.qa-note {
    margin-top: 8px;
    padding: 7px 10px;
    border-radius: 6px;
    background: rgba($infoColor, 0.08);
    color: darken($infoColor, 20%);
    font-size: 12px;
    font-weight: 600;
}
</style>
